/**
 * Static data + value contract for the Game Pass → Create Season wizard.
 * Each step component is a controlled view; the wizard parent
 * (`CreateSeason.tsx`) owns canonical state and uses these pure validators
 * to gate the per-step Next CTA.
 *
 * Follows the same single-source-of-truth shape as
 * `Affiliates/NewAffiliate/ContactInformation/contactInformationData.ts`.
 */

import dayjs, { type Dayjs } from "dayjs";

import {
  SEASON_API_STATUS,
  type SeasonApiRecord,
  type SeasonProgressionMode,
  type SeasonTaskSubmissionMode,
} from "../../../types/gamePass";

/** Hard cap on the Season Description textarea (mirrors the 0/500 counter
 *  shown in Season Overview). Co-located with the validator below so consumers of
 *  `isSeasonOverviewValid` and the rendered counter pull from the same
 *  source of truth. */
export const SEASON_DESCRIPTION_MAX = 500;

/** Hard cap on the Challenge Description textarea. */
export const DESCRIPTION_MAX = 500;

/** Hard cap on the Task Description textarea (Create Task modal). */
export const TASK_DESCRIPTION_MAX = 500;

/** Maps the admin-facing sequential toggle to the BE `progression_mode` wire
 *  value. On → `SEQUENTIAL`, off → `ANY_ORDER`. */
export const mapSequentialToProgressionMode = (
  sequential: boolean,
): SeasonProgressionMode => (sequential ? "SEQUENTIAL" : "ANY_ORDER");

/** Maps a stored `progression_mode` back to the sequential toggle. Missing or
 *  unknown values default to sequential (`true`). */
export const mapProgressionModeToSequential = (
  mode?: SeasonProgressionMode | string | null,
): boolean => mode !== "ANY_ORDER";

/** Free/Premium tier toggle pair shared by Step 1's checkbox row and the
 *  Edit Season modal's General section. */
export type SeasonTiersValue = {
  free: boolean;
  premium: boolean;
};

/** Single switch that gates the Premium tier across BOTH the Create Season
 *  wizard and the Edit Season modal (they share `SeasonOverview`). While
 *  `false`, the Premium checkbox stays locked off — except when the season
 *  being edited already has Premium enabled in the backend response, in which
 *  case it remains visible/editable so an existing premium season isn't
 *  silently downgraded. Flip this to `true` to unlock Premium everywhere. */
export const PREMIUM_TIER_ENABLED = false;

/** Default tier selection used by `SEASON_OVERVIEW_INITIAL_VALUES` and
 *  by `EditSeasonModal` when the parent doesn't pre-seed tiers. */
export const SEASON_TIERS_INITIAL_VALUE: SeasonTiersValue = {
  free: true,
  premium: true,
};

/** Step 1 — Season Overview form values.
 *
 * Both surfaces that render `SeasonOverview` (the Create Season wizard's
 * Step 1 and `EditSeasonModal`) drive these fields. Description has a
 * 50-char cap; at-least-one-tier-checked is required for `isSeasonOverviewValid`. */
export type SeasonOverviewValues = {
  name: string;
  description: string;
  tiers: SeasonTiersValue;
  /** Premium-tier price in gold coins. Kept as a free-form numeric string so
   *  empty / partial inputs are valid in-flight; only meaningful when
   *  `tiers.premium` is checked (the field is hidden otherwise). Required when
   *  the premium tier is enabled — see `isPremiumPriceGcValid`. */
  premiumPriceGc?: string;
  /** Premium-tier price in sweeps coins. Same free-form-string contract as
   *  `premiumPriceGc`, but optional even when the premium tier is enabled. */
  premiumPriceSc?: string;
  /** Default submission mode for sportsbook tasks in this season. */
  sportsbookSubmissionMode?: SeasonTaskSubmissionMode;
  /** Default submission mode for casino tasks — locked to AUTO in the UI. */
  casinoSubmissionMode?: SeasonTaskSubmissionMode;
  /** Uploaded season banner. The `FileDropzone` primitive owns preview-URL
   *  lifecycle. */
  imageFile?: File;
  /** S3 object key of an already-persisted season banner (set when editing a
   *  saved season). Used to rebuild the CloudFront preview URL — see
   *  `buildGamePassImageUrl`. A freshly picked `imageFile` takes precedence.
   *  `null` means the admin explicitly removed the persisted image, so the
   *  update payload sends `img_key: null` to clear it server-side. */
  imgKey?: string | null;
  /** Fully-qualified preview URL for a persisted season banner, taken
   *  verbatim from the BE `image_url` field. Preferred over rebuilding a URL
   *  from `imgKey`. A freshly picked `imageFile` still takes precedence. */
  imageUrl?: string | null;
};

// ─── Step 2 — Challenges & Tasks ───────────────────────────────────────────

/**
 * Reward type enum used by both Challenges and Tasks. Kept as a string union
 * so the Select can render labels by mapping over `REWARD_TYPE_OPTIONS`.
 */
export type RewardType =
  | "FREE_CASH_PICK"
  | "PROFIT_BOOST"
  | "REBET_CASH"
  | "REBET_COINS"
  | "XP"
  | "USD"
  | "NIRA";

export type RewardDraft = {
  id: string;
  /** Free-form numeric string — kept as string so empty / partial inputs are valid in-flight. */
  amount: string;
  /** The reward dropdown selection. For currency rewards this is a `RewardType`
   *  (e.g. `REBET_COINS`); for bonus rewards it's the BE bonus `reward_type`
   *  (e.g. `free_cash_pick`), so the union is widened to `string`. */
  type?: string;
  /** Bonus rewards only — the BE grant identifier (mirrors `type` for a bonus
   *  selection; absent for currency rewards). Its presence is what marks a row
   *  as a bonus reward on the wire. */
  bonusRewardType?: string;
  /** Bonus rewards only — the BE bonus `currency` (e.g. `free_pick`). */
  bonusCurrency?: string;
  /** Bonus rewards only — param name → user-entered value (kept as strings so
   *  partial input stays valid in-flight; coerced at the wire boundary). */
  params?: Record<string, string>;
};

/** Task → Trigger → Task Type (top pill row of the Create Task modal). */
export type TaskType = "SPORTSBOOK" | "SOCIALS" | "CASINO";

/** Casino "Action" pill — whether completing the task requires the user to
 *  simply Play a qualifying game or to Win on it. Shipped as the value of the
 *  BE `outcome` rule. */
export type CasinoAction = "PLAY" | "WIN";

/** Task → Trigger → Socials "Task Type" dropdown. Each option maps to a
 *  distinct social mechanic encoded on the wire as a `data_source: "SOCIAL"`
 *  task plus a condition rule (see `buildTaskRules` / `buildSocialRules`):
 *    - `FOLLOW_X_FOLLOWERS`     → Followers (FE-1638): `is_new_relationship`
 *    - `CONSECUTIVE_LOGIN_DAYS` → Consecutive Login Days (FE-1639):
 *                                 `consecutive_days gte X`
 *    - `P2P_WINS`               → P2P Wins (FE-1640):
 *                                 `opponent_account_age_days gte 1`
 *    - `CONSECUTIVE_PICK_DAYS`  → Consecutive Days Playing a Pick (FE-1641):
 *                                 `consecutive_days gte X` + `shared_device_flag`
 *  The login and pick streaks both carry `consecutive_days gte X`; the pick
 *  variant additionally emits a `shared_device_flag` marker that the login
 *  streak never sets, so the two stay distinguishable on read (see
 *  `resolveSocialTaskTypeFromRules`) and round-trip back to the exact dropdown
 *  selection on edit.
 *
 *  Kept as a string union so the Select can render labels by mapping over
 *  `SOCIAL_TASK_TYPE_OPTIONS`. */
export type SocialTaskType =
  | "FOLLOW_X_FOLLOWERS"
  | "CONSECUTIVE_LOGIN_DAYS"
  | "P2P_WINS"
  | "CONSECUTIVE_PICK_DAYS"
  // Surfaced when the Socials "Task Type" dropdown is driven by the backend
  // `social_event_types` catalogue (`user_referred`). Maps to a
  // `referred_user_made_qualifying_deposit` condition rule (see `buildSocialRules`).
  | "USER_REFERRED";

/** Task → Trigger → Type of Bet (pills under "Type of Bet"). `"ANY"`
 *  is the UI sentinel for "no restriction" and maps to `null` on the
 *  wire (`TaskBetType` in `types/gamePass.ts` intentionally excludes
 *  it). Everything else is a direct pass-through to the BE enum. */
export type BetType = "ANY" | "SINGLE" | "PARLAY" | "SGP" | "LIVE";

/** Task → Trigger → Bet Currency (multi-select). */
export type BetCurrency = "REBET_CASH" | "REBET_COINS";

/** Task → Trigger → Qualifier (pill row under "Qualifier"). Mirrors
 *  the BE `outcome_required` enum 1:1 (see `TaskOutcomeRequired`). */
export type TaskQualifier = "WIN" | "WIN_OR_PUSH" | "ANY_SETTLED";

/**
 * All the trigger fields for a task. The wizard's local-only validation
 * requires `minBetAmount`, at least one entry in `betCurrencies`, and a
 * `qualifier` pick — see `isTaskDraftValid`. `taskType` and `betType` are
 * always present because they default to the first pill in the group.
 *
 * `qualifier` is intentionally optional in the type — `undefined` is the
 * "not yet picked" sentinel that gates the Create button, mirroring the
 * empty-array sentinel used by `betCurrencies`.
 */
export type TaskTriggerDraft = {
  taskType: TaskType;
  betType: BetType;
  betCurrencies: ReadonlyArray<BetCurrency>;
  qualifier?: TaskQualifier;
  // ─── Optional "More Conditions" ─────────────────────────────────────────
  // Each is only populated once the user reveals its card via the matching
  // "+" pill. Kept as loose strings so partial inputs stay valid in-flight
  // (numeric guards live in `isTaskDraftValid`).
  //
  // `oddsRequirement` backs the "Minimum Odds" card. The value is American
  // odds (e.g. "-100" / "150"), so it may be negative — unlike a decimal
  // odds field. Maps to the BE `min_odds_american`.
  oddsRequirement?: string;
  // `maxOddsRequirement` backs the "Maximum Odds" card — same American-odds
  // semantics as `oddsRequirement` above. Maps to the BE `max_odds_american`.
  maxOddsRequirement?: string;
  /** "Minimum Bet Amount" input — the smallest qualifying stake for a
   *  sportsbook task. Ships as the top-level `min_bet_amount` column (NOT a
   *  rule). Kept loose (string) like the other numeric inputs; parsed at the
   *  mapper layer (`parseBetAmount`). */
  minBetAmount?: string;
  /** Currency unit shown beside the Minimum Bet Amount input (e.g. "USD").
   *  See `BET_CURRENCY_UNIT_OPTIONS`. UI-only today — there is no BE field
   *  for it yet, so the mappers don't ship it. */
  minBetCurrency?: string;
  /** Per-currency "Minimum Bet Amount" inputs, keyed by `BetCurrency`. When
   *  the admin selects one or more Bet Currencies, the modal renders a
   *  dedicated minimum-stake input for each so the floor can differ per
   *  currency (e.g. 100 Rebet Coins vs 0.5 Rebet Cash) instead of a single
   *  shared value. Kept loose (string) like the other numeric inputs. UI-only
   *  for now — the create/update mappers still read the legacy `minBetAmount`;
   *  shipping these per-currency values is a follow-up (payload phase). */
  minBetByCurrency?: Partial<Record<BetCurrency, string>>;
  /** Selected sport ids from the Sport Specific picker (e.g.
   *  `["sr:sport:1", "sr:sport:5"]`). Maps to the BE `sport_id` rule; the
   *  catalogue lives in `SPORT_SPECIFIC_OPTIONS` / `getSportOptions`. */
  sportSpecific?: ReadonlyArray<string>;
  /** Selected league / tournament ids from the dependent League dropdown in
   *  the Sport Specific condition (e.g. `["sr:tournament:17"]`). Maps to the
   *  BE `league_id` rule. */
  sportLeagues?: ReadonlyArray<string>;
  /** "Streak(days)" card value. UI-only today — there is no BE field for
   *  it yet, so the create/update mappers don't ship it. */
  streak?: string;
  /** "Number of Bets" input — the count of qualifying bets the user must place
   *  to complete a plain sportsbook (COUNT) task. Ships as the task's
   *  `target_value` when `counting_method` is `"COUNT"` (see
   *  `resolveTargetValue`). Mutually exclusive with the Streak card: entering a
   *  value here disables the Streak input and vice versa, since both drive the
   *  single `target_value`. Kept loose (string) like the other numeric inputs;
   *  parsed at the mapper layer. */
  numberOfBets?: string;
  /** Always-visible "XP Amount" field (below "More Conditions"). The XP
   *  awarded on task completion — maps to the BE `xp_reward`. Kept loose
   *  (string) like the other numeric inputs; parsed at the mapper layer. */
  xpAmount?: string;
  /** Per-task submission mode override. Sportsbook and casino tasks may
   *  choose Auto or Manual. Not surfaced for social tasks. Maps to BE
   *  `completion_mode` (`OPEN` / `USER_CHOICE`). */
  taskSubmissionMode?: SeasonTaskSubmissionMode;
  /** "Number of Legs" field — only shown (and only shipped) for multi-leg bet
   *  types (`"PARLAY"` / `"SGP"`, see `betTypeRequiresLegCount`). Maps to the
   *  BE `min_leg_count`. Kept loose (string) like the other numeric inputs;
   *  parsed at the mapper layer. */
  minLegCount?: string;
  // ─── Casino-only trigger fields ─────────────────────────────────────────
  // Surfaced only when the Task Type pill is "Casino". The provider / game
  // ids are the verbatim catalogue ids from `CASINO_PROVIDER_OPTIONS`, so
  // they round-trip straight back into the picker and ship as-is in the
  // task payload (`casino_providers` / `casino_games`).
  //
  /** Fully-selected casino provider ids (the provider-level checkbox). Maps
   *  to the BE `casino_providers` array. */
  casinoProviders?: ReadonlyArray<string>;
  /** Individually-selected casino game ids (per-game checkbox under an
   *  expanded provider). Maps to the BE `casino_games` array. */
  casinoGames?: ReadonlyArray<string>;
  /** Display names for {@link casinoGames}, keyed by game id. UI-only — not
   *  shipped on the wire — so selection chips stay readable after the modal
   *  closes and reopens (the BE stores ids only). */
  casinoGameLabels?: Readonly<Record<string, string>>;
  /** Casino "Action" pill — whether the user must Play or Win. Encoded as the
   *  BE `outcome` rule (Game Pass V2). Defaults to `"PLAY"`. */
  casinoAction?: CasinoAction;
  /** Free-text "Action Amount" beside the Action pill (e.g. number of
   *  plays / wins required). Shipped as the task's top-level `target_value`
   *  (NOT a rule). Kept loose (string) like the other numeric inputs; parsed
   *  at the mapper. */
  casinoActionAmount?: string;
  /** Casino "Bet Currency" multi-select (Rebet Cash / Rebet Coins). Encoded as
   *  the BE `money_type` rule (Game Pass V2) — `rebet_cash` / `rebet_coin`. */
  casinoBetCurrencies?: ReadonlyArray<BetCurrency>;
  /** Free-text "Wager Amount" for casino tasks (the wagered stake required to
   *  qualify). Ships on the BE top-level `min_bet_amount` column (outside
   *  `rules`) via `resolveMinBetAmount` — the casino counterpart to the
   *  sportsbook `minBetAmount`. Kept loose (string) like the other numeric
   *  inputs; parsed at the mapper. */
  casinoWagerAmount?: string;
  // ─── Socials-only trigger fields ────────────────────────────────────────
  // Surfaced only when the Task Type pill is "Socials". UI-only today — the
  // social-task API contract ships in a later PR, so the create/update
  // mappers don't read these yet (same pattern as `streak`).
  //
  /** Selected social task from the "Task Type" dropdown (e.g.
   *  `"FOLLOW_X_FOLLOWERS"`). See `SOCIAL_TASK_TYPE_OPTIONS`. Defaults to
   *  the first option when a Socials task is first authored. */
  socialTaskType?: SocialTaskType;
  /** Free-text "X Amount" beside the social Task Type dropdown (e.g. the
   *  number of followers to follow). Kept loose (string) like the other
   *  numeric inputs; parsed at the mapper layer when the API lands. */
  socialAmount?: string;
  /** The original `target_value` of an existing plain sportsbook task, captured
   *  by `mapApiTaskToDraft` on edit. The live "Number of Bets" input
   *  (`numberOfBets`) is the primary source for a COUNT task's goal, but this is
   *  retained as a fallback so re-saving a task whose `numberOfBets` input is
   *  left blank (e.g. an older "Place 25 bets" task edited without touching the
   *  field) keeps its stored goal intact instead of silently resetting it to the
   *  default. `undefined` for freshly authored tasks — they fall back to the
   *  default. */
  preservedTargetValue?: number;
};

export type TaskDraft = {
  id: string;
  name: string;
  description: string;
  /** Uploaded image. The `FileDropzone` primitive owns preview-URL lifecycle. */
  imageFile?: File;
  /** S3 object key of an already-persisted task image (set when editing a
   *  saved task). Used to rebuild the CloudFront preview URL — see
   *  `buildGamePassImageUrl`. A freshly picked `imageFile` takes precedence.
   *  `null` means the admin explicitly removed the persisted image, so the
   *  update payload sends `img_key: null` to clear it server-side. */
  imgKey?: string | null;
  /** Fully-qualified preview URL for a persisted task image, taken verbatim
   *  from the BE `image_url` field on the task create / update / list
   *  responses. Preferred over rebuilding a URL from `imgKey` (the old
   *  CloudFront-base reconstruction produced a broken preview). A freshly
   *  picked `imageFile` still takes precedence; this is the fallback for
   *  tasks loaded from the API or re-rendered after save. */
  imageUrl?: string | null;
  /** Zero-based position of this task within its parent challenge, mapped from
   *  the BE `challenge_order`. The backend stamps it on create (incrementing per
   *  task, so tasks default to creation order) and the drag-reorder flow
   *  overwrites it via `PUT /tasks/{task_id}`. This is the single ordering key —
   *  the task analogue of a challenge's `sort_order`. `null` only for legacy
   *  tasks created before the field existed — those sort last. See
   *  `sortTasksByChallengeOrder`. */
  challengeOrder?: number | null;
  trigger: TaskTriggerDraft;
  rewards: ReadonlyArray<RewardDraft>;
};

export type ChallengeDraft = {
  id: string;
  /** Server-assigned id returned by `POST /game-pass-admin/v2/admin/challenges`.
   *  Kept separate from the local `id` so React / dnd-kit keys stay stable
   *  across the optimistic editor lifecycle. */
  persistedId?: string;
  /** Which season path/tab this challenge belongs to inside the wizard. */
  pathAvailability: "FREE" | "PREMIUM" | "BOTH";
  name: string;
  description: string;
  /** Uploaded challenge icon. */
  iconFile?: File;
  /** S3 object key of an already-persisted challenge icon (set when editing a
   *  saved challenge). Used to rebuild the CloudFront preview URL — see
   *  `buildGamePassImageUrl`. A freshly picked `iconFile` takes precedence.
   *  `null` means the admin explicitly removed the persisted icon, so the
   *  update payload sends `img_key: null` to clear it server-side. */
  imgKey?: string | null;
  /** Fully-qualified preview URL for a persisted challenge icon, taken verbatim
   *  from the BE `image_url` field on the challenge create / update / list
   *  responses. Preferred over rebuilding a URL from `imgKey` (the old
   *  CloudFront-base reconstruction produced a broken preview). A freshly
   *  picked `iconFile` still takes precedence; this is the fallback for
   *  challenges loaded from the API or re-rendered after save. */
  imageUrl?: string | null;
  rewards: ReadonlyArray<RewardDraft>;
  tasks: ReadonlyArray<TaskDraft>;
  /** `true` when the editing form is open. Collapsed = summary view. */
  isExpanded: boolean;
  /** Becomes `true` after the first explicit `Save` click — drives the
   *  "Delete" button visibility and the summary-card affordances. */
  isSaved: boolean;
};

export type ChallengesValues = {
  sequential: boolean;
  /** When on, every task in the season grants the same XP reward instead of
   *  per-task XP values. UI-only for now — the persistence/behaviour wiring
   *  lands in a follow-up. */
  sameXpReward?: boolean;
  /** Shared XP amount applied to every task when `sameXpReward` is on. Kept
   *  as a sanitized integer string to match the per-task `xpAmount` field. */
  sameXpAmount?: string;
  challenges: ReadonlyArray<ChallengeDraft>;
};

// ─── Step 3 — Schedule ─────────────────────────────────────────────────────

/** Step 3 — Schedule values. Dates and times are split deliberately so each
 *  field can be edited independently in the 2×2 grid shown in Figma. */
export type ScheduleValues = {
  autoStartAfter: boolean;
  /** Legacy previous-season anchor retained for API records that still carry `trigger_season_id`. */
  previousSeasonId?: string;
  startDate?: Dayjs;
  startTime?: Dayjs;
  endDate?: Dayjs;
  endTime?: Dayjs;
};

// ─── Initial values ────────────────────────────────────────────────────────

export const SEASON_OVERVIEW_INITIAL_VALUES: SeasonOverviewValues = {
  name: "",
  description: "",
  // Premium tier is disabled in the Create Season wizard for now, so it
  // ships unchecked by default (Free-only). The shared
  // `SEASON_TIERS_INITIAL_VALUE` keeps both tiers on for the Edit flow.
  tiers: { ...SEASON_TIERS_INITIAL_VALUE, premium: false },
  premiumPriceGc: "",
  premiumPriceSc: "",
  sportsbookSubmissionMode: "AUTO",
  casinoSubmissionMode: "AUTO",
};

export const CHALLENGES_INITIAL_VALUES: ChallengesValues = {
  sequential: true,
  sameXpReward: false,
  challenges: [],
};

/**
 * Infers the "Same XP reward for all tasks" toggle from the tasks already
 * authored on a set of challenges. There is no backend field for the toggle
 * yet, so when seeding the editable Season Details view we derive it: if there
 * are at least TWO tasks and every task across all challenges grants the same
 * non-empty XP amount, the toggle is on and that value seeds the input;
 * otherwise it is off with an empty amount.
 *
 * The ≥2 floor is deliberate: "same XP for ALL tasks" is only a meaningful
 * claim once there is more than one task to share a value. A single task with
 * an XP value is just the normal per-task default — inferring the toggle on for
 * it would (via the season-level lock) wrongly freeze that task's XP field.
 */
export const deriveSameXpReward = (
  challenges: ReadonlyArray<ChallengeDraft>,
): { sameXpReward: boolean; sameXpAmount: string } => {
  const xpValues = challenges
    .flatMap((challenge) => challenge.tasks)
    .map((task) => task.trigger.xpAmount?.trim() ?? "");

  if (xpValues.length < 2) return { sameXpReward: false, sameXpAmount: "" };

  const [first, ...rest] = xpValues;
  if (!first) return { sameXpReward: false, sameXpAmount: "" };

  const allSame = rest.every((value) => value === first);
  return allSame
    ? { sameXpReward: true, sameXpAmount: first }
    : { sameXpReward: false, sameXpAmount: "" };
};

export const SCHEDULE_INITIAL_VALUES: ScheduleValues = {
  autoStartAfter: false,
};

/** Season-level defaults forwarded into the Create Task modal so new tasks
 *  inherit Step 1 / General submission settings until the admin overrides
 *  per task. */
export type TaskSubmissionModeDefaults = {
  sportsbook?: SeasonTaskSubmissionMode;
  casino?: SeasonTaskSubmissionMode;
};

/** Casino tasks are always Auto — mirrors Step 1 / `SeasonOverview`. */
export const CASINO_TASK_SUBMISSION_MODE: SeasonTaskSubmissionMode = "AUTO";

/** BE task-level wire values for sportsbook / casino submission mode.
 *  Auto → `OPEN`, Manual → `USER_CHOICE` (NOT `task_submission_mode`). */
export const TASK_COMPLETION_MODE = {
  OPEN: "OPEN",
  USER_CHOICE: "USER_CHOICE",
} as const;

export type TaskCompletionMode =
  (typeof TASK_COMPLETION_MODE)[keyof typeof TASK_COMPLETION_MODE];

export const mapTaskSubmissionModeToCompletionMode = (
  mode: SeasonTaskSubmissionMode,
): TaskCompletionMode =>
  mode === "MANUAL"
    ? TASK_COMPLETION_MODE.USER_CHOICE
    : TASK_COMPLETION_MODE.OPEN;

export const mapCompletionModeToTaskSubmissionMode = (
  completionMode: string | null | undefined,
): SeasonTaskSubmissionMode | undefined => {
  if (completionMode == null || completionMode === "") return undefined;
  if (completionMode === TASK_COMPLETION_MODE.USER_CHOICE) return "MANUAL";
  if (
    completionMode === TASK_COMPLETION_MODE.OPEN ||
    completionMode === "SEQUENTIAL"
  ) {
    // `SEQUENTIAL` was a mistaken earlier mapping — treat as Auto for reload.
    return "AUTO";
  }
  // Unknown values fall through to season defaults via
  // `resolveTaskSubmissionModeFromApiTask` — warn so new BE modes aren't silent.
  console.warn(
    `[GamePass] Unrecognized task completion_mode "${completionMode}"; falling back to season default.`,
  );
  return undefined;
};

/** Map a stamped task draft to the BE `completion_mode` for sportsbook / casino.
 *  Only explicit `trigger.taskSubmissionMode` values are sent — season
 *  defaults are display-only until Save stamps the form selection. */
export const resolveTaskCompletionMode = (
  draft: Pick<TaskDraft, "trigger">,
): TaskCompletionMode | undefined => {
  const { taskType, taskSubmissionMode } = draft.trigger;
  if (taskType === "SOCIALS") return undefined;
  if (taskType === "CASINO") {
    return mapTaskSubmissionModeToCompletionMode(CASINO_TASK_SUBMISSION_MODE);
  }
  if (taskType === "SPORTSBOOK" && taskSubmissionMode != null) {
    return mapTaskSubmissionModeToCompletionMode(taskSubmissionMode);
  }
  return undefined;
};

/** Resolve per-task submission mode from a task API record. Prefers
 *  `completion_mode` (`OPEN` / `USER_CHOICE`); falls back to legacy
 *  `task_submission_mode` when present. */
export const resolveTaskSubmissionModeFromApiTask = (
  taskType: TaskType,
  task: {
    completion_mode?: string | null;
    task_submission_mode?: SeasonTaskSubmissionMode | null;
  },
): SeasonTaskSubmissionMode | undefined => {
  if (taskType === "SOCIALS") return undefined;
  if (taskType === "CASINO") return CASINO_TASK_SUBMISSION_MODE;
  return (
    mapCompletionModeToTaskSubmissionMode(task.completion_mode) ??
    task.task_submission_mode ??
    undefined
  );
};

export const resolveDefaultTaskSubmissionMode = (
  taskType: TaskType,
  defaults: TaskSubmissionModeDefaults = {},
): SeasonTaskSubmissionMode | undefined => {
  if (taskType === "SPORTSBOOK") {
    return defaults.sportsbook ?? "AUTO";
  }
  if (taskType === "CASINO") {
    return CASINO_TASK_SUBMISSION_MODE;
  }
  return undefined;
};

/** Resolve the submission mode shown on the form at save time — the admin's
 *  explicit pill selection when set, otherwise the season default currently
 *  displayed for sportsbook / casino. */
export const resolveSubmittedTaskSubmissionMode = (
  draft: Pick<TaskDraft, "trigger">,
  defaults: TaskSubmissionModeDefaults = {},
): SeasonTaskSubmissionMode | undefined =>
  resolveTaskSubmissionModeFromRecord(
    draft.trigger.taskType,
    draft.trigger.taskSubmissionMode,
    defaults,
  );

/** Stamp the form's save-time submission mode onto a task draft before wire/API
 *  mapping or parent commit. Season defaults are display-only until save. */
export const stampTaskSubmissionModeAtSave = (
  draft: TaskDraft,
  defaults: TaskSubmissionModeDefaults = {},
): TaskDraft => {
  const taskSubmissionMode = resolveSubmittedTaskSubmissionMode(draft, defaults);
  if (taskSubmissionMode === undefined) return draft;
  return {
    ...draft,
    trigger: { ...draft.trigger, taskSubmissionMode },
  };
};

/** Resolve the editor's per-task submission mode from a task listing record.
 *  Explicit `task_submission_mode` values round-trip verbatim; when the BE
 *  omits the field (tasks inheriting the season default), fall back to the
 *  parent season's sportsbook / casino defaults instead of hardcoding AUTO. */
export const resolveTaskSubmissionModeFromRecord = (
  taskType: TaskType,
  taskSubmissionMode: SeasonTaskSubmissionMode | null | undefined,
  defaults: TaskSubmissionModeDefaults = {},
): SeasonTaskSubmissionMode | undefined => {
  if (taskType === "SOCIALS") return undefined;
  if (taskType === "CASINO") return CASINO_TASK_SUBMISSION_MODE;
  if (taskSubmissionMode != null) return taskSubmissionMode;
  return resolveDefaultTaskSubmissionMode(taskType, defaults);
};

/** Default values used for a fresh task draft (incl. Trigger). */
export const TASK_TRIGGER_INITIAL_VALUES: TaskTriggerDraft = {
  taskType: "SPORTSBOOK",
  betType: "ANY",
  betCurrencies: [],
};

// ─── Validators ────────────────────────────────────────────────────────────

/**
 * Whether Step 1 has the minimum data the wizard needs before advancing
 * (and the same gate the Edit Season modal's Save button uses for the
 * General section). Three rules, all must hold:
 *
 *   1. `name` is a non-empty trimmed string.
 *   2. `description`, when present, is ≤ `SEASON_DESCRIPTION_MAX` chars
 *      — defends against a programmatic seed that bypassed the textarea's
 *      `maxLength` (e.g. an API payload restored after a refresh).
 *   3. At least one tier (Free OR Premium) is checked.
 *
 * Both the wizard's Next button and `EditSeasonModal`'s Save button are
 * gated on this single function so the two surfaces stay in lock-step.
 */
export const isSeasonOverviewValid = (
  values: Partial<SeasonOverviewValues>,
): boolean => {
  if (typeof values.name !== "string" || values.name.trim() === "") {
    return false;
  }
  if (
    typeof values.description === "string" &&
    values.description.length > SEASON_DESCRIPTION_MAX
  ) {
    return false;
  }
  const tiers = values.tiers;
  if (!tiers || (!tiers.free && !tiers.premium)) {
    return false;
  }
  return true;
};

/** Parse a Premium Price free-text input (GC or SC) into the BE amount.
 *  Returns `null` — i.e. no premium price — when the premium tier is disabled
 *  or the input is blank / non-numeric / non-positive, so a season without a
 *  premium price never ships a `0` or `NaN`. The premium fields are hidden when
 *  the tier is off, so `premiumEnabled` keeps a stale value (never cleared on
 *  toggle) from leaking onto the wire. Shared by both the gold-coin and
 *  sweeps-coin inputs since the parse rule is identical. */
export const parsePremiumPrice = (
  value: string | undefined,
  premiumEnabled: boolean,
): number | null => {
  if (!premiumEnabled || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/** Whether the required Premium Price (GC) constraint is satisfied. GC is
 *  mandatory only when the premium tier is enabled; the sweeps-coin price stays
 *  optional. Kept separate from `isSeasonOverviewValid` (which is shared with
 *  `EditSeasonModal`, a surface that doesn't author the premium price) so the
 *  required-GC gate applies to the Create Season wizard only. */
export const isPremiumPriceGcValid = (
  values: Partial<SeasonOverviewValues>,
): boolean => {
  if (!values.tiers?.premium) return true;
  return parsePremiumPrice(values.premiumPriceGc, true) !== null;
};

/**
 * Step 2 has no validation beyond "must add at least one challenge" — the
 * sequential toggle is independent. Matches the disabled Next state shown
 * in the Figma screenshot where the empty challenges step has Next greyed
 * out.
 *
 * Note: UI-only — we don't gate on `isSaved`. Users can advance with an
 * in-edit challenge and the API layer (when it lands) will decide whether
 * to persist drafts on Next.
 */
export const hasAtLeastOneChallenge = (
  values: Partial<ChallengesValues>,
): boolean =>
  Array.isArray(values.challenges) && values.challenges.length > 0;

// ─── Numeric input helpers ─────────────────────────────────────────────────
//
// The numeric form fields are kept as loose strings so partial / empty inputs
// stay valid in-flight, but several of them must reject non-numeric text at
// the keystroke level (per product: "only a number field"). These pure
// sanitizers strip disallowed characters so the bound input can never hold a
// value the validators below would reject. They're exported so the components
// and the unit tests share a single source of truth.

/**
 * Strip everything except digits — used by the integer-only fields (XP Amount,
 * X Amount, Action Amount, Number of Legs, Streak). An empty string is
 * preserved so the field can always be cleared.
 */
export const sanitizeIntegerInput = (value: string): string =>
  value.replace(/[^0-9]/g, "");

/**
 * Keep digits plus a single leading decimal point — used by money-style amount
 * fields (reward amount, Minimum Bet Amount, casino Wager Amount) that can be
 * fractional (e.g. `2.50`). Any characters after the first decimal point that
 * are themselves dots are dropped so the value always parses with `Number`.
 */
export const sanitizeDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) +
    cleaned.slice(firstDot + 1).replace(/\./g, "")
  );
};

/**
 * Like {@link sanitizeDecimalInput} but keeps an optional leading sign — used by
 * the American-odds fields (Minimum / Maximum Odds), which may be positive
 * (`+150`) or negative (`-110`). Only a single `+`/`-` at the very start is
 * preserved; every other sign and non-numeric character is dropped, and the
 * decimal point is de-duplicated so the value always parses with `Number`.
 */
export const sanitizeSignedDecimalInput = (value: string): string => {
  const sign = value[0] === "+" || value[0] === "-" ? value[0] : "";
  return sign + sanitizeDecimalInput(sign ? value.slice(1) : value);
};

/** A required numeric string: non-empty and parsing to a finite, strictly
 *  positive number. Shared by the reward-amount and task-trigger validators so
 *  "required + must be a positive number" has one definition. */
const isRequiredPositiveNumber = (value?: string): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0;
};

// ─── Reward + Challenge validators ─────────────────────────────────────────

/** Minimal shape of a bonus reward's param definition needed to judge
 *  save-readiness. Mirrors the config option's param spec without importing it
 *  (`ChallengeRewardOption` lives in `gamePassOptions`, which already depends on
 *  this module — importing it back would be circular). */
export type RewardParamSpec = {
  name: string;
  type: string;
  required: boolean;
};

/** Whether a bonus-reward param value is a complete, well-formed entry for its
 *  declared type: an `integer` must be a whole number (optionally signed), a
 *  `number` must parse to a finite value, and every other type just needs to be
 *  non-blank. A partial signed entry like `"-"` is therefore NOT complete — it
 *  would ship as a non-numeric string and be rejected by the BE, so the Save
 *  gate must treat it as still-missing. Single source of truth shared by the
 *  card's inline param gate and {@link isRewardValid}. */
export const isParamValueComplete = (type: string, value?: string): boolean => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed === "") return false;
  if (type === "integer") return /^-?\d+$/.test(trimmed);
  if (type === "number") return Number.isFinite(Number(trimmed));
  return true;
};

/** Turn a snake_case bonus param name into a human label (e.g. `expiry_hours`
 *  → "Expiry hours"). Shared by Season and Monthly reward editors. */
export const humanizeParamName = (name: string): string => {
  const words = name.split(/[_\s]+/).filter(Boolean);
  if (words.length === 0) return name;
  return words
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
};

/** Keep param inputs typed to their declared kind: integers accept digits with
 *  an optional leading minus, numbers reuse the decimal sanitiser, everything
 *  else stays free text. */
export const sanitizeParamInput = (type: string, value: string): string => {
  if (type === "integer") {
    const sign = value.startsWith("-") ? "-" : "";
    return sign + value.slice(sign.length).replace(/[^\d]/g, "");
  }
  if (type === "number") return sanitizeDecimalInput(value);
  return value;
};

/** Seed a freshly-selected bonus reward's params: optional params prefill with
 *  their `default` (so the common case is one click), required params start
 *  blank so the Save gate holds until the admin fills them. */
export const buildDefaultRewardParams = (
  params?: ReadonlyArray<{
    name: string;
    required: boolean;
    default?: number | string | null;
  }>,
): Record<string, string> => {
  const seeded: Record<string, string> = {};
  for (const param of params ?? []) {
    if (!param.required && param.default !== null && param.default !== undefined) {
      seeded[param.name] = String(param.default);
    } else {
      seeded[param.name] = "";
    }
  }
  return seeded;
};

/**
 * A reward row is complete when it carries BOTH a reward type AND a positive
 * numeric amount. The amount is bound to a text input, so this is also what
 * enforces "reward amount must be a number, not text" at the gate (the
 * keystroke-level guard lives in `sanitizeDecimalInput`).
 *
 * Bonus rewards additionally carry tunable `params`. Pass the reward's param
 * spec (from its config option) via `requiredParams` to ALSO enforce that every
 * `required` param is present and well-formed; omit it (the default) to validate
 * only the reward row's type + amount.
 *
 * NOTE: the draft-only challenge validators
 * ({@link getMissingChallengeRequirements} / {@link isChallengeValid}) cannot
 * see the config param spec, so they check type + amount only. Any surface that
 * offers bonus rewards must gate Save on the config-derived required-param check
 * too (see `missingRewardParamLabels` in `ChallengeCard`).
 */
export const isRewardValid = (
  reward: Partial<RewardDraft>,
  requiredParams?: ReadonlyArray<RewardParamSpec>,
): boolean => {
  if (!reward?.type || !isRequiredPositiveNumber(reward?.amount)) return false;
  if (!requiredParams) return true;
  return requiredParams.every(
    (param) =>
      !param.required || isParamValueComplete(param.type, reward.params?.[param.name]),
  );
};

/** The required pieces of a Challenge, surfaced to the UI so the admin can be
 *  told exactly what's still missing rather than facing a silently-disabled
 *  Save button. */
export type ChallengeRequirementKey = "name" | "reward";

/**
 * The required-but-incomplete parts of a Challenge, in display order. Empty ⇒
 * the challenge is save-ready. This is the single source of truth for the Save
 * gate (`isChallengeValid` is derived from it) AND for the inline "what's
 * missing" hint the card renders next to a disabled Save button.
 *
 * Rules:
 *   1. `name` — a non-empty trimmed string.
 *   2. `reward` — at least one reward exists AND every reward row is complete
 *      (`isRewardValid`); a half-filled reward keeps the requirement open so no
 *      partial reward is ever persisted.
 *
 * Draft-only: this cannot see a bonus reward's config param spec, so it does NOT
 * validate required `params`. Bonus-capable surfaces must add the config-derived
 * required-param gate on top (see `missingRewardParamLabels` in `ChallengeCard`).
 */
export const getMissingChallengeRequirements = (
  challenge: Partial<ChallengeDraft>,
): ChallengeRequirementKey[] => {
  const missing: ChallengeRequirementKey[] = [];
  if (typeof challenge?.name !== "string" || challenge.name.trim() === "") {
    missing.push("name");
  }
  const rewards = challenge?.rewards;
  if (
    !Array.isArray(rewards) ||
    rewards.length === 0 ||
    !rewards.every((reward) => isRewardValid(reward))
  ) {
    missing.push("reward");
  }
  return missing;
};

/**
 * Whether a Challenge has the minimum data the wizard needs before its Save
 * CTA enables — derived from {@link getMissingChallengeRequirements} so the
 * gate and the inline hint can never disagree.
 *
 * Draft-only: like {@link getMissingChallengeRequirements}, this validates each
 * reward row's type + amount but NOT a bonus reward's required `params` (that
 * needs the config spec). A caller gating a bonus-capable surface on this alone
 * would miss the param check — pair it with the config-derived required-param
 * gate (`missingRewardParamLabels` in `ChallengeCard`).
 */
export const isChallengeValid = (
  challenge: Partial<ChallengeDraft>,
): boolean => getMissingChallengeRequirements(challenge).length === 0;

/**
 * Returns a new `challenges` array with the entry identified by `fromId`
 * moved into the slot currently held by `toId`. No-ops (returns the
 * original array reference) when either id is missing or when both ids
 * refer to the same challenge — keeps the dnd-kit `onDragEnd` callsite
 * concise and lets React skip a re-render in the common "drop on self"
 * case.
 *
 * Lives in `createSeasonData.ts` so it can be unit-tested in isolation
 * without mounting a `<DndContext>` — the dnd-kit code stays at the
 * component layer while the reorder math is a pure function.
 */
export const reorderChallenges = (
  challenges: ReadonlyArray<ChallengeDraft>,
  fromId: string,
  toId: string,
): ReadonlyArray<ChallengeDraft> => {
  if (fromId === toId) return challenges;
  const fromIndex = challenges.findIndex((c) => c.id === fromId);
  const toIndex = challenges.findIndex((c) => c.id === toId);
  if (fromIndex === -1 || toIndex === -1) return challenges;
  const next = [...challenges];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * Sibling of `reorderChallenges` for the per-challenge task list. Same
 * fast-path semantics: returns the original `tasks` reference when the
 * move is a no-op (self-drop or unknown id) so React can skip a
 * re-render. Used by `ChallengeCard`'s task drag wiring.
 */
export const reorderTasks = (
  tasks: ReadonlyArray<TaskDraft>,
  fromId: string,
  toId: string,
): ReadonlyArray<TaskDraft> => {
  if (fromId === toId) return tasks;
  const fromIndex = tasks.findIndex((t) => t.id === fromId);
  const toIndex = tasks.findIndex((t) => t.id === toId);
  if (fromIndex === -1 || toIndex === -1) return tasks;
  const next = [...tasks];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

/**
 * Orders a task list for display within its parent challenge by its single
 * ordering key, `challengeOrder` (BE `challenge_order`) — the task analogue of
 * how challenges sort by `sort_order`.
 *
 * The backend stamps `challenge_order` on create (incrementing per task) and
 * the drag-reorder flow overwrites it, so this one key covers both the default
 * "by creation" order and any manual reorder. Tasks ascend by `challengeOrder`;
 * legacy tasks missing the field (`null`/`undefined`) sink to the bottom in
 * their original order via a stable sort.
 *
 * Pure (returns a new array, input untouched) so it can be unit-tested without
 * mounting the editor — same convention as `reorderTasks`.
 */
export const sortTasksByChallengeOrder = (
  tasks: ReadonlyArray<TaskDraft>,
): ReadonlyArray<TaskDraft> => {
  // Missing order sinks to the bottom, preserving input order among those.
  const challengeOrderOf = (value: number | null | undefined): number =>
    typeof value === "number" && Number.isFinite(value)
      ? value
      : Number.POSITIVE_INFINITY;
  return tasks
    .map((task, index) => ({ task, index }))
    .sort((a, b) => {
      const aOrder = challengeOrderOf(a.task.challengeOrder);
      const bOrder = challengeOrderOf(b.task.challengeOrder);
      if (aOrder !== bOrder) return aOrder - bOrder;
      // Tied (or both missing) → stable: preserve original order.
      return a.index - b.index;
    })
    .map(({ task }) => task);
};

/** One attempted `challenge_order` write from a drag-reorder: the task `id` and
 *  the new zero-based position the UI tried to persist for it. */
export type AttemptedTaskChallengeOrder = {
  id: string;
  index: number;
};

/**
 * Reconcile an optimistic task drag-reorder against the *actual* result of the
 * per-task `challenge_order` PUTs, so a partial failure leaves the frontend
 * order consistent with what the backend now holds.
 *
 * Each reorder fans out into one PUT per moved task. Because those PUTs are
 * independent (`Promise.allSettled`), some can succeed while others fail — a
 * naive "roll the whole list back on any error" both contradicts the writes
 * that DID land and produces an order that matches neither the pre- nor the
 * post-drag state on the next reload (FE-1727).
 *
 * This computes each task's *effective* `challenge_order` — the value the
 * backend will return on reload:
 *   - a task whose PUT succeeded → its newly-persisted index (`attempted`),
 *   - every other task (unchanged, a skipped local-only id, or a *failed* PUT)
 *     → its pre-reorder `challengeOrder`, which the backend still holds.
 * The list is then re-sorted by that effective order (nulls last), tie-broken
 * by the pre-reorder order — the closest proxy to the order the backend returns
 * for rows that share an index or are unordered. The result mirrors exactly
 * what `sortTasksByChallengeOrder` would produce on the next load.
 *
 * Pure (no mutation of the inputs) so it can be unit-tested without mounting
 * the editor — same convention as `reorderTasks` / `sortTasksByChallengeOrder`.
 */
export const reconcileTaskReorder = (
  previousTasks: ReadonlyArray<TaskDraft>,
  attempted: ReadonlyArray<AttemptedTaskChallengeOrder>,
  failedIds: ReadonlySet<string>,
): ReadonlyArray<TaskDraft> => {
  const attemptedIndexById = new Map(
    attempted.map(({ id, index }) => [id, index]),
  );
  return previousTasks
    .map((task, previousIndex) => {
      const attemptedIndex = attemptedIndexById.get(task.id);
      const persisted =
        attemptedIndex !== undefined && !failedIds.has(task.id);
      const effectiveChallengeOrder = persisted
        ? attemptedIndex
        : (task.challengeOrder ?? null);
      return {
        // Only stamp the new order when the write actually landed; failed /
        // unchanged tasks keep their object (and stored `challengeOrder`) as-is.
        task: persisted ? { ...task, challengeOrder: attemptedIndex } : task,
        previousIndex,
        effectiveChallengeOrder,
      };
    })
    .sort((a, b) => {
      const aHas = a.effectiveChallengeOrder !== null;
      const bHas = b.effectiveChallengeOrder !== null;
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (
        aHas &&
        bHas &&
        a.effectiveChallengeOrder !== b.effectiveChallengeOrder
      ) {
        return (
          (a.effectiveChallengeOrder as number) -
          (b.effectiveChallengeOrder as number)
        );
      }
      return a.previousIndex - b.previousIndex;
    })
    .map((entry) => entry.task);
};

/**
 * Step 3 is valid when either auto-start is on, or all four date/time fields
 * are filled. Previous-season selection is no longer authored from this UI.
 */
export const isScheduleValid = (values: Partial<ScheduleValues>): boolean => {
  if (!values) return false;
  if (values.autoStartAfter) return true;
  if (
    !values.startDate ||
    !values.startTime ||
    !values.endDate ||
    !values.endTime
  ) {
    return false;
  }
  // Compose full Date+Time Dayjs values so the comparison respects both
  // calendar day AND hour/minute. Without this, "end day = start day" with
  // an end time earlier than start time would slip through, and the server
  // would reject the season once persistence is wired.
  const start = values.startDate
    .startOf("day")
    .hour(values.startTime.hour())
    .minute(values.startTime.minute());
  const end = values.endDate
    .startOf("day")
    .hour(values.endTime.hour())
    .minute(values.endTime.minute());
  return end.isAfter(start);
};

// ─── Auto-start scheduling (FE-1970) ───────────────────────────────────────

/** Gap between the anchor season's end and the auto-started season's start. The
 *  new season begins exactly one minute after the previous one ends (anchor ends
 *  12:30 → auto-start begins 12:31) so the two run back-to-back with no overlap. */
export const AUTO_START_GAP_MINUTES = 1;

/** Default length pre-filled for the (editable) end date when auto-start is
 *  enabled — start + this many days. The admin can still adjust the end. */
export const AUTO_START_DEFAULT_DURATION_DAYS = 3;

/** The predecessor an auto-started season chains after — the currently ACTIVE
 *  season. `id` becomes the new draft's `trigger_season_id`; `end` drives the
 *  auto-filled start (one minute later). */
export type AutoStartAnchor = {
  /** The active season's id → patched onto the new draft as `trigger_season_id`
   *  so the BE knows which season this one follows. `null` only if the record
   *  carries no id (defensive — a real record always has one). */
  id: string | null;
  /** The active season's end instant → the new season starts one minute after. */
  end: Dayjs;
};

/**
 * Resolve the predecessor a new auto-started season should chain after: the
 * currently ACTIVE season. Per the BE contract (FE-1970), the new draft is
 * patched to start one minute after this season's `end_date` and links to it
 * via `trigger_season_id`. Returns `null` when no active season exists (nothing
 * to chain after). The (defensive) "latest end wins" tie-break handles the
 * never-expected case of more than one active season.
 *
 * `excludeSeasonId` skips the draft currently being authored so it can never
 * anchor to itself. Pure (no React) so it can be unit-tested in isolation.
 */
export const resolveAutoStartAnchor = (
  seasons: ReadonlyArray<SeasonApiRecord>,
  excludeSeasonId?: string | null,
): AutoStartAnchor | null => {
  let anchor: AutoStartAnchor | null = null;
  for (const record of seasons) {
    const recordId = record.id ?? record.season_id ?? null;
    if (excludeSeasonId && recordId === excludeSeasonId) continue;
    if (record.status !== SEASON_API_STATUS.ACTIVE) continue;
    if (!record.end_date) continue;
    const end = dayjs(record.end_date);
    if (!end.isValid()) continue;
    if (!anchor || end.isAfter(anchor.end)) anchor = { id: recordId, end };
  }
  return anchor;
};

/** The auto-start date/time slice derived from an anchor end instant. */
export type AutoStartScheduleFill = Pick<
  ScheduleValues,
  "startDate" | "startTime" | "endDate" | "endTime"
>;

/**
 * Build the auto-start date/time fill from the anchor season's end instant: the
 * new season starts exactly `AUTO_START_GAP_MINUTES` after the anchor ends and
 * defaults to an `AUTO_START_DEFAULT_DURATION_DAYS`-long window. `startDate` /
 * `startTime` share the (locked) start instant; `endDate` / `endTime` share the
 * (editable) end instant. Returns all-`undefined` when there is no valid anchor
 * so the caller can leave the grid empty until the seasons list resolves.
 */
export const buildAutoStartSchedule = (
  anchorEnd: Dayjs | null | undefined,
): AutoStartScheduleFill => {
  if (!anchorEnd || !anchorEnd.isValid()) {
    return {
      startDate: undefined,
      startTime: undefined,
      endDate: undefined,
      endTime: undefined,
    };
  }
  const start = anchorEnd
    .add(AUTO_START_GAP_MINUTES, "minute")
    .second(0)
    .millisecond(0);
  const end = start.add(AUTO_START_DEFAULT_DURATION_DAYS, "day");
  return { startDate: start, startTime: start, endDate: end, endTime: end };
};

// ─── Factories ─────────────────────────────────────────────────────────────

/**
 * Builds a sequential challenge name used as the placeholder label when the
 * user clicks "Add Challenge". The label matches the figma "Challenge 1",
 * "Challenge 2", … pattern.
 */
export const buildPlaceholderChallengeName = (
  prefix: string,
  index: number,
): string => `${prefix} ${index}`;

/**
 * Build a stable id with a counter suffix so two adds in the same ms (jsdom
 * synchronous user-event clicks share a tick) still produce unique keys.
 */
const buildLocalId = (prefix: string, index: number): string =>
  `${prefix}-${Date.now()}-${index}`;

export const createEmptyReward = (index = 0): RewardDraft => ({
  id: buildLocalId("reward", index),
  amount: "",
});

export const createEmptyChallenge = (
  index: number,
  prefix: string,
  pathAvailability: "FREE" | "PREMIUM" | "BOTH" = "FREE",
): ChallengeDraft => ({
  id: buildLocalId("challenge", index),
  pathAvailability,
  name: buildPlaceholderChallengeName(prefix, index),
  description: "",
  rewards: [createEmptyReward(0)],
  tasks: [],
  isExpanded: true,
  isSaved: false,
});

export const createEmptyTask = (
  index: number,
  overrides: Partial<TaskDraft> = {},
): TaskDraft => ({
  id: buildLocalId("task", index),
  name: "",
  description: "",
  trigger: { ...TASK_TRIGGER_INITIAL_VALUES },
  rewards: [createEmptyReward(0)],
  ...overrides,
});

/**
 * Clone a task draft for challenge duplication. The copy is a brand-new,
 * not-yet-persisted task: it takes a fresh local id (so it never aliases the
 * source task's server id in React / dnd-kit keys or the `/tasks/{task_id}`
 * URL) and resets `challengeOrder` (the duplicated challenge's save flow
 * re-stamps it by position). Everything the admin configured is preserved —
 * name, description, trigger and rewards — and the image is carried over by
 * reference (`imgKey` / `imageUrl`), or as a not-yet-uploaded `imageFile`, so
 * the duplicate keeps its artwork. Rewards are re-keyed for the same
 * key-stability reason as the task id; the (shallow) trigger copy guards
 * against the source and copy sharing a mutable reference.
 */
export const duplicateTaskDraft = (
  task: TaskDraft,
  index: number,
): TaskDraft => ({
  ...task,
  id: buildLocalId("task", index),
  challengeOrder: undefined,
  trigger: { ...task.trigger },
  rewards: task.rewards.map((reward, rewardIndex) => ({
    ...reward,
    id: buildLocalId("reward", rewardIndex),
  })),
});

/** Static option lists used by the Select / pill components. */
export const REWARD_TYPE_OPTIONS: ReadonlyArray<{
  value: RewardType;
  labelKey:
    | "REWARD_TYPE_FREE_CASH_PICK"
    | "REWARD_TYPE_PROFIT_BOOST"
    | "REWARD_TYPE_REBET_CASH"
    | "REWARD_TYPE_REBET_COINS"
    | "REWARD_TYPE_XP"
    | "REWARD_TYPE_USD"
    | "REWARD_TYPE_NIRA";
}> = [
  { value: "FREE_CASH_PICK", labelKey: "REWARD_TYPE_FREE_CASH_PICK" },
  { value: "PROFIT_BOOST", labelKey: "REWARD_TYPE_PROFIT_BOOST" },
  { value: "REBET_CASH", labelKey: "REWARD_TYPE_REBET_CASH" },
  { value: "REBET_COINS", labelKey: "REWARD_TYPE_REBET_COINS" },
  { value: "XP", labelKey: "REWARD_TYPE_XP" },
  { value: "USD", labelKey: "REWARD_TYPE_USD" },
  { value: "NIRA", labelKey: "REWARD_TYPE_NIRA" },
];

/** Maximum rewards a challenge (or task) can carry — one per distinct reward
 *  type. Derived from `REWARD_TYPE_OPTIONS` so the cap can never drift from the
 *  number of types the picker actually offers. The "Add another Reward" CTA is
 *  hidden once a challenge has this many rows. */
export const MAX_REWARDS = REWARD_TYPE_OPTIONS.length;

export const TASK_TYPE_OPTIONS: ReadonlyArray<{
  value: TaskType;
  labelKey:
    | "CREATE_TASK_TASK_TYPE_SPORTSBOOK"
    | "CREATE_TASK_TASK_TYPE_SOCIALS"
    | "CREATE_TASK_TASK_TYPE_CASINO";
}> = [
  { value: "SPORTSBOOK", labelKey: "CREATE_TASK_TASK_TYPE_SPORTSBOOK" },
  { value: "SOCIALS", labelKey: "CREATE_TASK_TASK_TYPE_SOCIALS" },
  { value: "CASINO", labelKey: "CREATE_TASK_TASK_TYPE_CASINO" },
];

export const BET_TYPE_OPTIONS: ReadonlyArray<{
  value: BetType;
  labelKey:
    | "CREATE_TASK_BET_TYPE_ANY"
    | "CREATE_TASK_BET_TYPE_SINGLE"
    | "CREATE_TASK_BET_TYPE_PARLAY"
    | "CREATE_TASK_BET_TYPE_SGP"
    | "CREATE_TASK_BET_TYPE_LIVE";
}> = [
  { value: "ANY", labelKey: "CREATE_TASK_BET_TYPE_ANY" },
  { value: "SINGLE", labelKey: "CREATE_TASK_BET_TYPE_SINGLE" },
  { value: "PARLAY", labelKey: "CREATE_TASK_BET_TYPE_PARLAY" },
  { value: "SGP", labelKey: "CREATE_TASK_BET_TYPE_SGP" },
  { value: "LIVE", labelKey: "CREATE_TASK_BET_TYPE_LIVE" },
];

/** Bet types that surface (and ship) the Number of Legs input. Both Parlay and
 *  SGP (same-game parlay) are multi-leg bets, so each requires a leg count;
 *  SINGLE / LIVE and the `"ANY"` sentinel never do. Single source of truth
 *  shared by the modal (render + clear-on-switch), the required-field validator,
 *  and the create / update mappers so the gate can't drift between them. */
export const betTypeRequiresLegCount = (betType: BetType): boolean =>
  betType === "PARLAY" || betType === "SGP";

/** Minimum Number of Legs for the multi-leg bet types (Parlay / SGP). A single
 *  leg can't form a parlay or same-game parlay, so the leg count must be at
 *  least 2. Shared by the required-field validator and the modal's inline hint
 *  so the rule has a single source of truth. */
export const MIN_LEG_COUNT = 2;

export const BET_CURRENCY_OPTIONS: ReadonlyArray<{
  value: BetCurrency;
  labelKey:
    | "CREATE_TASK_BET_CURRENCY_REBET_CASH"
    | "CREATE_TASK_BET_CURRENCY_REBET_COINS";
}> = [
  { value: "REBET_CASH", labelKey: "CREATE_TASK_BET_CURRENCY_REBET_CASH" },
  { value: "REBET_COINS", labelKey: "CREATE_TASK_BET_CURRENCY_REBET_COINS" },
];

/** Map the UI Bet Currency pill (`BetCurrency`) onto the BE `money_type` rule
 *  value. The wire values are the canonical lowercase keys the platform uses
 *  everywhere else (`rebet_cash` / `rebet_coin`), so a casino task's
 *  `money_type` rule matches the rest of the system. */
export const BET_CURRENCY_TO_MONEY_TYPE: Record<BetCurrency, string> = {
  REBET_CASH: "rebet_cash",
  REBET_COINS: "rebet_coin",
};

/** Inverse of `BET_CURRENCY_TO_MONEY_TYPE` — resolves a BE `money_type` wire
 *  value back to the UI Bet Currency pill so a saved casino task round-trips
 *  into the modal's multi-select on edit. */
export const MONEY_TYPE_TO_BET_CURRENCY: Record<string, BetCurrency> = {
  rebet_cash: "REBET_CASH",
  rebet_coin: "REBET_COINS",
};

export const TASK_QUALIFIER_OPTIONS: ReadonlyArray<{
  value: TaskQualifier;
  labelKey:
    | "CREATE_TASK_QUALIFIER_WIN"
    | "CREATE_TASK_QUALIFIER_WIN_OR_PUSH"
    | "CREATE_TASK_QUALIFIER_ANY_SETTLED";
}> = [
  { value: "WIN", labelKey: "CREATE_TASK_QUALIFIER_WIN" },
  { value: "WIN_OR_PUSH", labelKey: "CREATE_TASK_QUALIFIER_WIN_OR_PUSH" },
  { value: "ANY_SETTLED", labelKey: "CREATE_TASK_QUALIFIER_ANY_SETTLED" },
];

/** Currency-unit options shown beside the Minimum Bet Amount input. Static
 *  + UI-only for now (no BE field yet) — kept here so the modal reads from
 *  a single source of truth. The first entry is the default unit. */
export const BET_CURRENCY_UNIT_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "CAD", label: "CAD" },
];

/** Default unit selected when the Minimum Bet Amount condition is added. */
export const DEFAULT_BET_CURRENCY_UNIT = BET_CURRENCY_UNIT_OPTIONS[0].value;

// ─── Sport Specific catalogue + selection helpers ──────────────────────────
//
// The "Sport Specific" condition is a two-level tree-select: top-level
// sports, each with child leagues, both individually selectable. The
// catalogue is static placeholder data for now (the real sport/league list
// ships from the sportsbook config later); kept as a constant so the modal,
// the payload mapper, and the unit tests all read the same source of truth.
// The `value` ids are exactly what end up in the task's `sports` payload.

/** A single league entry under a sport in the Sport Specific tree. */
export type SportLeagueOption = {
  /** Tournament id (or static catalogue token) stored on `trigger.sportLeagues`
   *  and shipped as the `league_id` rule. */
  value: string;
  label: string;
  /** BE tournament id when provided by task-options (may duplicate `value`). */
  tournamentId?: string;
};

/** A top-level sport in the Sport Specific tree, with its child leagues. */
export type SportOption = {
  /** Sport id/value sent in the `sport_id` rule (e.g. `"sr:sport:4"`). */
  value: string;
  label: string;
  leagues: ReadonlyArray<SportLeagueOption>;
};

export const SPORT_SPECIFIC_OPTIONS: ReadonlyArray<SportOption> = [
  {
    value: "BASEBALL",
    label: "Baseball",
    leagues: [
      { value: "MLB", label: "MLB" },
      { value: "NPB", label: "NPB" },
      { value: "KBO", label: "KBO" },
    ],
  },
  {
    value: "BASKETBALL",
    label: "Basketball",
    leagues: [
      { value: "NBA", label: "NBA" },
      { value: "WNBA", label: "WNBA" },
      { value: "NCAAB", label: "NCAAB" },
    ],
  },
  {
    value: "FOOTBALL",
    label: "Football",
    leagues: [
      { value: "NFL", label: "NFL" },
      { value: "NCAAF", label: "NCAAF" },
    ],
  },
  {
    value: "HOCKEY",
    label: "Hockey",
    leagues: [
      { value: "NHL", label: "NHL" },
      { value: "AHL", label: "AHL" },
      { value: "KHL", label: "KHL" },
    ],
  },
  {
    value: "SOCCER",
    label: "Soccer",
    leagues: [
      { value: "EPL", label: "Premier League" },
      { value: "LALIGA", label: "La Liga" },
      { value: "SERIE_A", label: "Serie A" },
      { value: "BUNDESLIGA", label: "Bundesliga" },
      { value: "MLS", label: "MLS" },
      { value: "UCL", label: "Champions League" },
    ],
  },
  {
    value: "TENNIS",
    label: "Tennis",
    leagues: [
      { value: "ATP", label: "ATP" },
      { value: "WTA", label: "WTA" },
      { value: "GRAND_SLAM", label: "Grand Slam" },
    ],
  },
];

/**
 * Selection model for the Sport Specific picker (see helpers below).
 *
 * Only top-level sports are selectable — leagues are intentionally not
 * exposed in the UI. The selection is therefore a flat `string[]` of sport
 * ids that the modal stores on `trigger.sportSpecific` and ships verbatim
 * as the task's `sports` payload (e.g. `["HOCKEY", "SOCCER"]`).
 *
 * The helpers are pure so they can be unit-tested without mounting the
 * dropdown — same convention as `reorderChallenges` / `reorderTasks`.
 */
export const isSportSelected = (
  selection: ReadonlyArray<string>,
  sport: SportOption,
): boolean => selection.includes(sport.value);

/** Toggle a sport on/off, returning the next selection array. */
export const toggleSport = (
  selection: ReadonlyArray<string>,
  sport: SportOption,
): ReadonlyArray<string> =>
  isSportSelected(selection, sport)
    ? selection.filter((id) => id !== sport.value)
    : [...selection, sport.value];

/**
 * League selection model for the dependent League dropdown. Leagues are tracked
 * as a flat `string[]` of tournament ids (or static catalogue values) stored on
 * `trigger.sportLeagues` and shipped as the task's `league_id` rule.
 */
export const isLeagueSelected = (
  selection: ReadonlyArray<string>,
  league: SportLeagueOption,
): boolean => selection.includes(league.value);

/** Toggle a league on/off, returning the next selection array. */
export const toggleLeague = (
  selection: ReadonlyArray<string>,
  league: SportLeagueOption,
): ReadonlyArray<string> =>
  isLeagueSelected(selection, league)
    ? selection.filter((id) => id !== league.value)
    : [...selection, league.value];

// ─── Casino provider / game catalogue + selection helpers ──────────────────
//
// The "Casino Provider and Games" picker is a two-level tree: top-level
// providers (each with its own checkbox) and, once expanded, the provider's
// games grouped by a sub-label (e.g. "Blackjack", "Roulette"). Both
// providers and games are independently selectable. A category tab strip
// (All Providers / Table Games / Slots / Live Casino / Jackpot) filters the
// games shown by their `category`.
//
// The catalogue is static placeholder data for now (the real provider/game
// list ships from the casino config later — tracked in FE-1591); kept as a
// constant so the modal, the payload mapper, and the unit tests all read the
// same source of truth.
// The `value` ids are exactly what end up in the task's `casino_providers`
// and `casino_games` payload arrays.

/** Top-level casino category used by the picker's tab strip and per-game
 *  `category` field. `"ALL"` is the picker default (no filter) and is not a
 *  real game category — it's the "All Providers" tab sentinel. */
export type CasinoCategory =
  | "ALL"
  | "TABLE_GAMES"
  | "SLOTS"
  | "LIVE_CASINO"
  | "JACKPOT";

/** A single game entry under a provider in the Casino picker. */
export type CasinoGameOption = {
  /** Stable id sent in the `casino_games` payload (e.g. `"INFINITE_BLACKJACK"`). */
  value: string;
  label: string;
  /** Sub-group header rendered inside the expanded provider (e.g.
   *  "Blackjack", "Roulette"). Purely presentational grouping. */
  group: string;
  /** Top-level category the game belongs to — drives the tab filter. Never
   *  `"ALL"` (that's the tab sentinel, not a real category). */
  category: Exclude<CasinoCategory, "ALL">;
};

/** A top-level casino provider in the picker, with its child games. */
export type CasinoProviderOption = {
  /** Stable id sent in the `casino_providers` payload (e.g. `"EVOLUTION"`). */
  value: string;
  label: string;
  games: ReadonlyArray<CasinoGameOption>;
};

export const CASINO_PROVIDER_OPTIONS: ReadonlyArray<CasinoProviderOption> = [
  {
    value: "PRAGMATIC_PLAY",
    label: "Pragmatic Play",
    games: [
      {
        value: "GATES_OF_OLYMPUS",
        label: "Gates of Olympus",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "SWEET_BONANZA",
        label: "Sweet Bonanza",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "BIG_BASS_BONANZA",
        label: "Big Bass Bonanza",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "MEGA_ROULETTE",
        label: "Mega Roulette",
        group: "Live Casino",
        category: "LIVE_CASINO",
      },
    ],
  },
  {
    value: "EVOLUTION",
    label: "Evolution Gaming",
    games: [
      {
        value: "INFINITE_BLACKJACK",
        label: "Infinite Blackjack",
        group: "Blackjack",
        category: "LIVE_CASINO",
      },
      {
        value: "SPEED_BLACKJACK",
        label: "Speed Blackjack",
        group: "Blackjack",
        category: "LIVE_CASINO",
      },
      {
        value: "FREE_BET_BLACKJACK",
        label: "Free Bet Blackjack",
        group: "Blackjack",
        category: "LIVE_CASINO",
      },
      {
        value: "AUTO_ROULETTE",
        label: "Auto Roulette",
        group: "Roulette",
        category: "LIVE_CASINO",
      },
      {
        value: "LIGHTNING_ROULETTE",
        label: "Lightning Roulette",
        group: "Roulette",
        category: "LIVE_CASINO",
      },
      {
        value: "VIP_ROULETTE",
        label: "VIP Roulette",
        group: "Roulette",
        category: "LIVE_CASINO",
      },
      {
        value: "BACCARAT_SQUEEZE",
        label: "Baccarat Squeeze",
        group: "Baccarat",
        category: "TABLE_GAMES",
      },
      {
        value: "CRAPS",
        label: "Craps",
        group: "Other",
        category: "TABLE_GAMES",
      },
    ],
  },
  {
    value: "NETENT",
    label: "NetEnt",
    games: [
      {
        value: "STARBURST",
        label: "Starburst",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "GONZOS_QUEST",
        label: "Gonzo's Quest",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "DIVINE_FORTUNE",
        label: "Divine Fortune",
        group: "Jackpot",
        category: "JACKPOT",
      },
    ],
  },
  {
    value: "PLAYTECH",
    label: "Playtech",
    games: [
      {
        value: "AGE_OF_THE_GODS",
        label: "Age of the Gods",
        group: "Jackpot",
        category: "JACKPOT",
      },
      {
        value: "BUFFALO_BLITZ",
        label: "Buffalo Blitz",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "PLAYTECH_BLACKJACK",
        label: "Blackjack",
        group: "Table Games",
        category: "TABLE_GAMES",
      },
    ],
  },
  {
    value: "MICROGAMING",
    label: "Microgaming",
    games: [
      {
        value: "MEGA_MOOLAH",
        label: "Mega Moolah",
        group: "Jackpot",
        category: "JACKPOT",
      },
      {
        value: "MAJOR_MILLIONS",
        label: "Major Millions",
        group: "Jackpot",
        category: "JACKPOT",
      },
      {
        value: "IMMORTAL_ROMANCE",
        label: "Immortal Romance",
        group: "Slots",
        category: "SLOTS",
      },
    ],
  },
  {
    value: "HACKSAW",
    label: "Hacksaw Gaming",
    games: [
      {
        value: "WANTED_DEAD_OR_A_WILD",
        label: "Wanted Dead or a Wild",
        group: "Slots",
        category: "SLOTS",
      },
      {
        value: "LE_BANDIT",
        label: "Le Bandit",
        group: "Slots",
        category: "SLOTS",
      },
    ],
  },
];

/** Tab strip shown above the provider list. The first entry (`ALL`) is the
 *  default and renders every provider/game unfiltered. */
export const CASINO_CATEGORY_TABS: ReadonlyArray<{
  value: CasinoCategory;
  labelKey:
    | "CREATE_TASK_CASINO_TAB_ALL"
    | "CREATE_TASK_CASINO_TAB_TABLE_GAMES"
    | "CREATE_TASK_CASINO_TAB_SLOTS"
    | "CREATE_TASK_CASINO_TAB_LIVE_CASINO"
    | "CREATE_TASK_CASINO_TAB_JACKPOT";
}> = [
  { value: "ALL", labelKey: "CREATE_TASK_CASINO_TAB_ALL" },
  { value: "TABLE_GAMES", labelKey: "CREATE_TASK_CASINO_TAB_TABLE_GAMES" },
  { value: "SLOTS", labelKey: "CREATE_TASK_CASINO_TAB_SLOTS" },
  { value: "LIVE_CASINO", labelKey: "CREATE_TASK_CASINO_TAB_LIVE_CASINO" },
  { value: "JACKPOT", labelKey: "CREATE_TASK_CASINO_TAB_JACKPOT" },
];

/** Action pill options (Play / Win) shown beside "Action Amount". */
export const CASINO_ACTION_OPTIONS: ReadonlyArray<{
  value: CasinoAction;
  labelKey: "CREATE_TASK_ACTION_PLAY" | "CREATE_TASK_ACTION_WIN";
}> = [
  { value: "PLAY", labelKey: "CREATE_TASK_ACTION_PLAY" },
  { value: "WIN", labelKey: "CREATE_TASK_ACTION_WIN" },
];

/** Default Action pill selection when a Casino task is first authored. */
export const DEFAULT_CASINO_ACTION: CasinoAction = "PLAY";

// ─── Socials task type catalogue ───────────────────────────────────────────
//
// The "Socials" Task Type swaps the sportsbook/casino trigger fields for a
// single "Task Type" dropdown plus an "X Amount" input. Each option maps to a
// `data_source: "SOCIAL"` task with a condition rule (see `buildSocialRules`)
// and the "X Amount" → `target_value`. Kept as a constant so the modal reads
// from a single source of truth, mirroring the other option lists.

/** Social "Task Type" dropdown options (FE-1638 … FE-1641). The first entry is
 *  the default selected when a Socials task is first authored. */
export const SOCIAL_TASK_TYPE_OPTIONS: ReadonlyArray<{
  value: SocialTaskType;
  labelKey:
    | "CREATE_TASK_SOCIAL_TASK_TYPE_FOLLOW_X_FOLLOWERS"
    | "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_LOGIN_DAYS"
    | "CREATE_TASK_SOCIAL_TASK_TYPE_P2P_WINS"
    | "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_PICK_DAYS";
}> = [
  {
    value: "FOLLOW_X_FOLLOWERS",
    labelKey: "CREATE_TASK_SOCIAL_TASK_TYPE_FOLLOW_X_FOLLOWERS",
  },
  {
    value: "CONSECUTIVE_LOGIN_DAYS",
    labelKey: "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_LOGIN_DAYS",
  },
  {
    value: "P2P_WINS",
    labelKey: "CREATE_TASK_SOCIAL_TASK_TYPE_P2P_WINS",
  },
  {
    value: "CONSECUTIVE_PICK_DAYS",
    labelKey: "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_PICK_DAYS",
  },
];

/** Default social task type selected when a Socials task is first authored. */
export const DEFAULT_SOCIAL_TASK_TYPE = SOCIAL_TASK_TYPE_OPTIONS[0].value;

/** Label key for every `SocialTaskType`, including the API-only
 *  `USER_REFERRED`. The static `SOCIAL_TASK_TYPE_OPTIONS` covers the original
 *  four; this record additionally labels `USER_REFERRED` so the API-driven
 *  dropdown (built from `social_event_types`) can render every mapped option.
 *  Keyed exhaustively so adding a `SocialTaskType` is a compile error here
 *  until its label is decided. */
export const SOCIAL_TASK_TYPE_LABEL_KEY: Record<
  SocialTaskType,
  | "CREATE_TASK_SOCIAL_TASK_TYPE_FOLLOW_X_FOLLOWERS"
  | "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_LOGIN_DAYS"
  | "CREATE_TASK_SOCIAL_TASK_TYPE_P2P_WINS"
  | "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_PICK_DAYS"
  | "CREATE_TASK_SOCIAL_TASK_TYPE_USER_REFERRED"
> = {
  FOLLOW_X_FOLLOWERS: "CREATE_TASK_SOCIAL_TASK_TYPE_FOLLOW_X_FOLLOWERS",
  CONSECUTIVE_LOGIN_DAYS: "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_LOGIN_DAYS",
  P2P_WINS: "CREATE_TASK_SOCIAL_TASK_TYPE_P2P_WINS",
  CONSECUTIVE_PICK_DAYS: "CREATE_TASK_SOCIAL_TASK_TYPE_CONSECUTIVE_PICK_DAYS",
  USER_REFERRED: "CREATE_TASK_SOCIAL_TASK_TYPE_USER_REFERRED",
};

/** Maps a backend `social_event_types` entry onto the UI `SocialTaskType` that
 *  carries its rule semantics (see `buildSocialRules`). The dropdown options
 *  are built from this map so the Socials "Task Type" list is API-driven while
 *  the rule encoding / round-trip stays keyed on the stable internal enum.
 *  Events with no UI counterpart are skipped (the picker only offers what we
 *  can encode). */
export const SOCIAL_EVENT_TYPE_TO_SOCIAL_TASK_TYPE: Record<
  string,
  SocialTaskType
> = {
  user_followed: "FOLLOW_X_FOLLOWERS",
  daily_login: "CONSECUTIVE_LOGIN_DAYS",
  p2p_pick_won: "P2P_WINS",
  user_referred: "USER_REFERRED",
  // No entry for CONSECUTIVE_PICK_DAYS (FE-1641): the backend has not yet
  // shipped a `social_event_types` key for the "Consecutive Days Playing a
  // Pick" mechanic. The internal type stays fully wired (static catalogue,
  // buildSocialRules, resolveSocialTaskTypeFromRules) so existing tasks keep
  // round-tripping; add the mapping here the moment the BE event name lands.
};

/** Whether a provider's checkbox is ticked (the whole provider selected). */
export const isCasinoProviderSelected = (
  providers: ReadonlyArray<string>,
  provider: CasinoProviderOption,
): boolean => providers.includes(provider.value);

/** Whether an individual game's checkbox is ticked. */
export const isCasinoGameSelected = (
  games: ReadonlyArray<string>,
  game: CasinoGameOption,
): boolean => games.includes(game.value);

/** Toggle a provider on/off, returning the next provider-id array. */
export const toggleCasinoProvider = (
  providers: ReadonlyArray<string>,
  provider: CasinoProviderOption,
): ReadonlyArray<string> =>
  isCasinoProviderSelected(providers, provider)
    ? providers.filter((id) => id !== provider.value)
    : [...providers, provider.value];

/** Toggle an individual game on/off, returning the next game-id array. */
export const toggleCasinoGame = (
  games: ReadonlyArray<string>,
  game: CasinoGameOption,
): ReadonlyArray<string> =>
  isCasinoGameSelected(games, game)
    ? games.filter((id) => id !== game.value)
    : [...games, game.value];

/**
 * Filter the catalogue for the active category tab. `"ALL"` returns the
 * catalogue untouched; any other category returns only the providers that
 * own at least one game in that category, each narrowed to just those games.
 * Pure so the tab logic can be unit-tested without mounting the picker.
 */
export const filterCasinoProvidersByCategory = (
  category: CasinoCategory,
  providers: ReadonlyArray<CasinoProviderOption> = CASINO_PROVIDER_OPTIONS,
): ReadonlyArray<CasinoProviderOption> => {
  if (category === "ALL") return providers;
  return providers
    .map((provider) => ({
      ...provider,
      games: provider.games.filter((game) => game.category === category),
    }))
    .filter((provider) => provider.games.length > 0);
};

/** The required fields of a Create Task draft, surfaced to the UI so the admin
 *  can be told exactly which inputs are still empty instead of facing a
 *  silently-disabled Save/Create button (e.g. when editing a legacy task saved
 *  before these fields became mandatory). Keys are stable; the modal maps them
 *  to localized labels. */
export type TaskRequirementKey =
  | "name"
  | "minBetAmount"
  | "betCurrencies"
  | "qualifier"
  | "minLegCount"
  | "xpAmount"
  | "casinoSelection"
  | "casinoActionAmount"
  | "casinoBetCurrencies"
  | "socialAmount";

/**
 * The required-but-incomplete fields of a Create Task draft, in display order,
 * gated per Task Type (the modal swaps its trigger surface off
 * `trigger.taskType`):
 *   - SPORTSBOOK → Minimum Bet Amount, Bet Currency, Qualifier, XP Amount (and,
 *     for the multi-leg bet types Parlay / SGP, Number of Legs). Type of Bet
 *     always has a default pill.
 *   - SOCIALS    → X Amount, XP Amount (Task Type has a default dropdown pick).
 *   - CASINO     → a provider/game selection, Action Amount, Bet Currency, XP
 *     Amount (the Action pill has a default).
 * Every required numeric amount must parse to a strictly positive number, so
 * "is only a number field" is enforced here as well as at the keystroke
 * (`sanitizeIntegerInput`). This is the single source of truth for the required
 * fields: {@link isTaskDraftValid} derives its required-field check from it, and
 * the modal renders the same list as an inline "what's missing" hint.
 */
/**
 * The effective minimum-bet floor for a sportsbook task, given the bet
 * currencies the admin selected and the per-currency minimums sourced from
 * `GET /config` (`config.currencies[*].min_bet`, mapped to UI `BetCurrency`
 * keys by `getBetCurrencyMinimums`). A single "Minimum Bet Amount" field backs
 * every selected currency, so the floor is the LARGEST of the selected
 * currencies' minimums — clearing the strictest currency clears them all (e.g.
 * Rebet Coins → 100 dominates Rebet Cash → 0.5). Returns `undefined` when no
 * selected currency carries a positive configured minimum, leaving the field
 * unconstrained (so the gate is a no-op until the catalogue ships a `min_bet`).
 */
export const getMinBetAmountFloor = (
  betCurrencies: ReadonlyArray<BetCurrency> | undefined,
  currencyMinimums?: Partial<Record<BetCurrency, number>>,
): number | undefined => {
  if (!betCurrencies || !currencyMinimums) return undefined;
  let floor: number | undefined;
  for (const currency of betCurrencies) {
    const min = currencyMinimums[currency];
    if (typeof min === "number" && Number.isFinite(min) && min > 0) {
      floor = floor === undefined ? min : Math.max(floor, min);
    }
  }
  return floor;
};

/**
 * Whether the sportsbook "Minimum Bet Amount" satisfies the per-currency floor
 * for the currently selected bet currencies. `true` when there is no floor
 * (no constrained currency selected), when the field is still empty (a missing
 * value is reported by {@link getMissingTaskRequirements}, not here), or when
 * the entered amount is ≥ the floor. Only a populated value strictly below the
 * floor is rejected — that's the case the ticket enforces (e.g. 50 entered
 * while Rebet Coins, min 100, is selected).
 */
export const isMinBetAmountAboveFloor = (
  minBetAmount: string | undefined,
  betCurrencies: ReadonlyArray<BetCurrency> | undefined,
  currencyMinimums?: Partial<Record<BetCurrency, number>>,
): boolean => {
  const floor = getMinBetAmountFloor(betCurrencies, currencyMinimums);
  if (floor === undefined) return true;
  const trimmed = typeof minBetAmount === "string" ? minBetAmount.trim() : "";
  if (trimmed === "") return true;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return true;
  return parsed >= floor;
};

/**
 * Whether a single per-currency Minimum Bet Amount input clears that currency's
 * own configured floor. Mirrors {@link isMinBetAmountAboveFloor} but scoped to
 * one currency (the per-currency UI renders one input per selected currency).
 * `true` when the currency has no positive configured minimum, when the input
 * is still empty (a missing value is reported separately), or when the typed
 * amount is ≥ that currency's floor. Only a populated value strictly below the
 * floor is rejected.
 */
export const isCurrencyMinBetAboveFloor = (
  value: string | undefined,
  currency: BetCurrency,
  currencyMinimums?: Partial<Record<BetCurrency, number>>,
): boolean => {
  const min = currencyMinimums?.[currency];
  if (typeof min !== "number" || !Number.isFinite(min) || min <= 0) return true;
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (trimmed === "") return true;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return true;
  return parsed >= min;
};

/**
 * Whether every selected sportsbook Bet Currency has a per-currency Minimum Bet
 * Amount that clears its own floor. With no currency selected (or no configured
 * minimums) there is nothing to enforce, so this returns `true`. Used by
 * {@link isTaskDraftValid} to gate the per-currency minimum-bet inputs.
 */
export const areCurrencyMinBetsAboveFloor = (
  minBetByCurrency: Partial<Record<BetCurrency, string>> | undefined,
  betCurrencies: ReadonlyArray<BetCurrency> | undefined,
  currencyMinimums?: Partial<Record<BetCurrency, number>>,
): boolean => {
  if (!betCurrencies || betCurrencies.length === 0) return true;
  return betCurrencies.every((currency) =>
    isCurrencyMinBetAboveFloor(
      minBetByCurrency?.[currency],
      currency,
      currencyMinimums,
    ),
  );
};

export const getMissingTaskRequirements = (
  task: Partial<TaskDraft>,
): TaskRequirementKey[] => {
  const missing: TaskRequirementKey[] = [];
  if (typeof task?.name !== "string" || task.name.trim() === "") {
    missing.push("name");
  }
  const trigger = task?.trigger;
  if (!trigger) return missing;

  if (trigger.taskType === "CASINO") {
    const hasCasinoSelection =
      (Array.isArray(trigger.casinoProviders) &&
        trigger.casinoProviders.length > 0) ||
      (Array.isArray(trigger.casinoGames) && trigger.casinoGames.length > 0);
    if (!hasCasinoSelection) missing.push("casinoSelection");
    if (!isRequiredPositiveNumber(trigger.casinoActionAmount)) {
      missing.push("casinoActionAmount");
    }
    if (
      !Array.isArray(trigger.casinoBetCurrencies) ||
      trigger.casinoBetCurrencies.length === 0
    ) {
      missing.push("casinoBetCurrencies");
    }
    if (!isRequiredPositiveNumber(trigger.xpAmount)) missing.push("xpAmount");
    return missing;
  }

  if (trigger.taskType === "SOCIALS") {
    if (!isRequiredPositiveNumber(trigger.socialAmount)) {
      missing.push("socialAmount");
    }
    if (!isRequiredPositiveNumber(trigger.xpAmount)) missing.push("xpAmount");
    return missing;
  }

  // SPORTSBOOK (default). Minimum Bet Amount is intentionally mandatory — it is
  // the qualifying stake floor, and it coexists with the optional "Number of
  // Bets" input (which drives `target_value`; see `resolveTargetValue`).
  // This also gates legacy sportsbook tasks created before the column existed:
  // editing one surfaces "Minimum Bet Amount" as required (via the inline
  // "what's missing" hint, not a silently-disabled button), so the admin is told
  // exactly what to set before saving rather than being able to persist a task
  // with no minimum stake. This is by design, not a regression.
  const hasBetCurrencies =
    Array.isArray(trigger.betCurrencies) && trigger.betCurrencies.length > 0;
  // Minimum Bet Amount is now captured per selected currency: every chosen Bet
  // Currency needs its own positive minimum. Until a currency is picked there's
  // nothing to enter, so the field reads as missing (the Bet Currency
  // requirement below covers the "pick a currency first" nudge). The legacy
  // single `minBetAmount` is still honoured as a fallback for drafts authored
  // before the per-currency inputs existed.
  const hasEveryCurrencyMinBet =
    hasBetCurrencies &&
    trigger.betCurrencies.every((currency) =>
      isRequiredPositiveNumber(trigger.minBetByCurrency?.[currency]),
    );
  if (
    !hasEveryCurrencyMinBet &&
    !isRequiredPositiveNumber(trigger.minBetAmount)
  ) {
    missing.push("minBetAmount");
  }
  if (!hasBetCurrencies) {
    missing.push("betCurrencies");
  }
  if (!trigger.qualifier) missing.push("qualifier");
  // Number of Legs is required (and must be an integer ≥ MIN_LEG_COUNT) only
  // for multi-leg bet types (Parlay / SGP) — the field is shown solely for
  // those. A single leg can't form a parlay or same-game parlay, so 1 is
  // rejected just like an empty / non-numeric value.
  if (betTypeRequiresLegCount(trigger.betType)) {
    const trimmedLegs =
      typeof trigger.minLegCount === "string" ? trigger.minLegCount.trim() : "";
    const parsedLegs = Number(trimmedLegs);
    if (
      trimmedLegs === "" ||
      !Number.isFinite(parsedLegs) ||
      !Number.isInteger(parsedLegs) ||
      parsedLegs < MIN_LEG_COUNT
    ) {
      missing.push("minLegCount");
    }
  }
  if (!isRequiredPositiveNumber(trigger.xpAmount)) missing.push("xpAmount");
  return missing;
};

/** Whether a Create Task draft has enough data to be submitted. Required-field
 *  gating is derived from {@link getMissingTaskRequirements} (single source of
 *  truth, also used for the inline hint); the optional Minimum/Maximum Odds
 *  cards add value-level guards (numeric, non-zero, coherent range) on top. */
export const isTaskDraftValid = (
  task: Partial<TaskDraft>,
  currencyMinimums?: Partial<Record<BetCurrency, number>>,
): boolean => {
  if (!task) return false;
  const trigger = task.trigger;
  if (!trigger) return false;
  if (getMissingTaskRequirements(task).length > 0) return false;
  // Sportsbook only: each per-currency Minimum Bet Amount must clear its own
  // floor from `GET /config` (e.g. ≥ 100 for Rebet Coins, ≥ 0.5 for Rebet
  // Cash). Casino / socials tasks have no Minimum Bet Amount field, so they
  // skip this gate.
  //
  // The per-currency map is the source of truth once it covers every selected
  // currency. The legacy single `minBetAmount` is ONLY a fallback for drafts
  // authored before the per-currency inputs existed (where the map is
  // absent/empty) — it must never be checked alongside the per-currency values,
  // or a stale legacy value left behind on a freshly edited task would block an
  // otherwise-valid draft.
  if (trigger.taskType !== "CASINO" && trigger.taskType !== "SOCIALS") {
    const selectedCurrencies = trigger.betCurrencies ?? [];
    const hasCurrencyMinBets =
      selectedCurrencies.length > 0 &&
      selectedCurrencies.every(
        (currency) => (trigger.minBetByCurrency?.[currency] ?? "").trim() !== "",
      );
    const floorOk = hasCurrencyMinBets
      ? areCurrencyMinBetsAboveFloor(
          trigger.minBetByCurrency,
          trigger.betCurrencies,
          currencyMinimums,
        )
      : isMinBetAmountAboveFloor(
          trigger.minBetAmount,
          trigger.betCurrencies,
          currencyMinimums,
        );
    if (!floorOk) return false;
  }
  // `oddsRequirement` (the Minimum Odds card) is optional, but when the
  // user types into it we need a numeric guard so "abc" / "0" don't slip
  // through as an invalid string. The value is American odds, which may be
  // negative (e.g. "-100"), so only non-numbers and exactly zero are
  // rejected. Empty / omitted stays valid because the card is optional. A
  // lone "-" is in-flight tolerance (the user is mid-typing).
  if (
    typeof trigger.oddsRequirement === "string" &&
    trigger.oddsRequirement.trim() !== ""
  ) {
    const trimmedOdds = trigger.oddsRequirement.trim();
    if (trimmedOdds !== "-" && trimmedOdds !== "+") {
      const parsedOdds = Number(trimmedOdds);
      if (!Number.isFinite(parsedOdds) || parsedOdds === 0) return false;
    }
  }
  // `maxOddsRequirement` (the Maximum Odds card) gets the same American-odds
  // guard as `oddsRequirement` above: non-numbers and exactly zero are
  // rejected; negatives are allowed; empty / lone "-" stays valid.
  if (
    typeof trigger.maxOddsRequirement === "string" &&
    trigger.maxOddsRequirement.trim() !== ""
  ) {
    const trimmedMaxOdds = trigger.maxOddsRequirement.trim();
    if (trimmedMaxOdds !== "-" && trimmedMaxOdds !== "+") {
      const parsedMaxOdds = Number(trimmedMaxOdds);
      if (!Number.isFinite(parsedMaxOdds) || parsedMaxOdds === 0) return false;
    }
  }
  // Cross-field guard: when both the Minimum and Maximum Odds cards hold a
  // real value (not empty, not the lone "-" in-flight sentinel), the range
  // must be coherent. `maxOdds < minOdds` describes an impossible window —
  // no bet can be both ≥ minOdds and ≤ maxOdds — so reject it client-side
  // instead of shipping a contradictory odds constraint to the API.
  if (
    typeof trigger.oddsRequirement === "string" &&
    trigger.oddsRequirement.trim() !== "" &&
    trigger.oddsRequirement.trim() !== "-" &&
    trigger.oddsRequirement.trim() !== "+" &&
    typeof trigger.maxOddsRequirement === "string" &&
    trigger.maxOddsRequirement.trim() !== "" &&
    trigger.maxOddsRequirement.trim() !== "-" &&
    trigger.maxOddsRequirement.trim() !== "+"
  ) {
    const minOdds = Number(trigger.oddsRequirement.trim());
    const maxOdds = Number(trigger.maxOddsRequirement.trim());
    if (Number.isFinite(minOdds) && Number.isFinite(maxOdds) && maxOdds < minOdds) {
      return false;
    }
  }
  return true;
};
